import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as CommitteeMemberActions from "../../../actions/committeeMember";
import * as CommitteeActions from "../../../actions/committees";
import * as NavigationActions from "../../../actions/navigation";
import { NotificationAlert } from "../../../components/common/index";
import { TextAreaFormInput } from "../../../components/form";
import { SelectFormInput } from "../../../components/form/index";
import { RestrictedLayout } from "../../../components/layouts";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { CommitteeMemberCreateBreadcrumbs, CommitteeMemberDetailsBreadcrumbs } from "../../../helpers/breadcrumbHelper";
import { RegistrationComponent, renderFormControls } from "../../../helpers/formHelper";
import { CommitteeMemberSubMenu } from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { ICommitteeMemberReduxFormState } from "../../../reducers/reactReduxForms/committeeMember";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";

import "./CommitteeMemberPage.scss";

interface CommitteeMemberPageProps {
    form: Dtos.CommitteeMember & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    reduxFormState: ICommitteeMemberReduxFormState;
    committeeMember: Dtos.CommitteeMember;
    committee: Dtos.Committee | undefined;
    lookups: Dtos.Lookup[];
    loadingCommittee: boolean;
    loadCommitteeSuccess: boolean;
    loadCommitteeFailure: boolean;
    loadCommitteeErrorCode: string;
    loadingCommitteeMember: boolean;
    loadCommitteeMemberSuccess: boolean;
    loadCommitteeMemberFailure: boolean;
    loadCommitteeMemberErrorCode: string;
    savingCommitteeMember: boolean;
    saveCommitteeMemberSuccess: boolean;
    saveCommitteeMemberFailure: boolean;
    creatingCommitteeMember: boolean;
    createCommitteeMemberSuccess: boolean;
    createCommitteeMemberFailure: boolean;
    validatingCommitteeMember: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];   
    clearCommitteeMember: CommitteeMemberActions.ICommitteeMemberClearActionCreator;
    loadCommitteeMember: CommitteeMemberActions.ICommitteeMemberLoadByIdActionCreator;
    saveCommitteeMember: CommitteeMemberActions.ICommitteeMemberSaveActionCreator;
    createCommitteeMember: CommitteeMemberActions.ICommitteeMemberCreateActionCreator;
    validateCommitteeMember: CommitteeMemberActions.ICommitteeMemberValidateActionCreator;
    clearCommittee: CommitteeActions.ICommitteeClearActionCreator;
    loadCommittee: CommitteeActions.ICommitteeLoadByIdActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const reduxFormName: string = "reduxForms.committeeMember";

const CommitteeMemberPage: FC<CommitteeMemberPageProps> = ({
    form,
    formState,
    formProperties,
    //reduxFormState,
    committeeMember,
    committee,
    lookups,
    loadingCommittee,
    //loadCommitteeSuccess,
    loadCommitteeFailure,
    loadCommitteeErrorCode,
    loadingCommitteeMember,
    //loadCommitteeMemberSuccess,
    loadCommitteeMemberFailure,
    loadCommitteeMemberErrorCode,
    savingCommitteeMember,
    saveCommitteeMemberSuccess,
    saveCommitteeMemberFailure,
    creatingCommitteeMember,
    //createCommitteeMemberSuccess,
    //createCommitteeMemberFailure,
    validatingCommitteeMember,
    validationFailures,
    permissions,
    clearCommitteeMember,
    loadCommitteeMember,
    saveCommitteeMember,
    createCommitteeMember,
    validateCommitteeMember,
    clearCommittee,
    loadCommittee,
    loadForm,
    changeForm,
    resetForm,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { committeeId } = useParams<{ committeeId: string }>();
    const { committeeMemberId } = useParams<{ committeeMemberId: string }>();
    const committeeIdNumber = parseInt(committeeId, 10);
    const committeeMemberIdNumber = parseInt(committeeMemberId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initalised, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.CommitteeMember = {} as Dtos.CommitteeMember;

        loadForm(reduxFormName, Object.assign({ ...committeeMember }, extraData));
    };


    // Load/Create committee member
    useEffect(() => {
        if (committeeMemberIdNumber && !committeeMember && !loadingCommitteeMember) {
            setDrawerItems(CommitteeMemberSubMenu(committeeIdNumber, committeeMemberIdNumber, permissions));
            setBreadcrumbs(
                CommitteeMemberDetailsBreadcrumbs(
                    committeeIdNumber,
                    committeeMemberIdNumber
                )
            );
            loadCommitteeMember(committeeMemberIdNumber);
        } 
        else if (!committeeMemberIdNumber && !committeeMember && !creatingCommitteeMember) {
            setDrawerItems([]);
            setBreadcrumbs(CommitteeMemberCreateBreadcrumbs(committeeIdNumber));
            createCommitteeMember(committeeIdNumber);
        } else if (committeeMember && !initalised) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        committeeIdNumber,
        committeeMemberIdNumber,
        committeeMember,
        loadingCommitteeMember,
        permissions,
        creatingCommitteeMember,
        initalised
    ]);

    // Load committee
    useEffect(() => {
        if (committeeIdNumber && !committee && !loadingCommittee) {
            loadCommittee(committeeIdNumber);
        }
    }, [committeeIdNumber, committee, loadingCommittee]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadCommitteeFailure) {
            navigate(`/error/${loadCommitteeErrorCode}`);
        }
        if (loadCommitteeMemberFailure) {
            navigate(`/error/${loadCommitteeMemberErrorCode}`);
        }
    }, [
        loadCommitteeFailure, 
        loadCommitteeErrorCode,
        loadCommitteeMemberFailure,
        loadCommitteeMemberErrorCode
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (committeeMemberIdNumber && committeeIdNumber && committeeMember && committee) {
            setBreadcrumbs(
                CommitteeMemberDetailsBreadcrumbs(
                    committeeIdNumber,
                    committeeMemberIdNumber,
                    committee.name,
                    committeeMember?.personnel?.displayWithSalutation
                )
            );
        }
        else if (!committeeMemberIdNumber && committeeIdNumber && committee) {
            setBreadcrumbs(
                CommitteeMemberCreateBreadcrumbs(committeeIdNumber, committee.name)
            );
        }
    }, [
        committeeMemberIdNumber, 
        committeeIdNumber,
        committeeMember,
        committee
    ]);

    // Reload committee member after saving
    useEffect(() => {
        if (!savingCommitteeMember && saveCommitteeMemberSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Committee Member saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            if (saveAndReturn) {
                navigate(`/committees/${committeeIdNumber}/members`);
            } 
            else if (!committeeMemberIdNumber) {
                navigate(`/committees/${committeeIdNumber}/members/${committeeMember.id}/details`);
                clearCommitteeMember();
            }
        }
    }, [
        savingCommitteeMember,
        saveCommitteeMemberSuccess,
        saveAndReturn,
        committeeIdNumber,
        committeeMember
    ]);

    // Failed saving
    useEffect(() => {
        if (!savingCommitteeMember && saveCommitteeMemberFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [savingCommitteeMember, saveCommitteeMemberFailure]);

    // Cleanup effect to clear the committee member on component unmount
    useEffect(() => {
        return () => {
            clearCommitteeMember();
            clearCommittee();
            resetForm(reduxFormName);
        };
    }, [clearCommitteeMember, clearCommittee, resetForm, reduxFormName]);

    const onCancel = () => {
        navigate(`/committees/${committeeIdNumber}/members`);
    };

    const handleOnSubmitFailed = (_data: Dtos.CommitteeMember) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.CommitteeMember = {} as Dtos.CommitteeMember;

            await saveCommitteeMember(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.CommitteeMember = {} as Dtos.CommitteeMember;

            if (form && hasBeenSubmitted && !validatingCommitteeMember) {
                await validateCommitteeMember(Object.assign({ ...form }, changes));
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const hasPermission: boolean = AuthenticationHelper.hasPermission(
            permissions,
            Dtos.Permission.CommitteeMemberUpdate
        );
        const formDisabled: boolean =
            !hasPermission ||
            savingCommitteeMember ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <h1>Details</h1>
                        <div className="card card-shadow">
                            <div className="form-row py-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "committeeMember",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-1",
                                                fieldClassName: "col-sm-11 generic-form-field mr-0 pr-0",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
            commentGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <br />
                        <h1>Comments</h1>
                        <div className="card card-shadow">
                            <div className="form-row pt-3 pb-4 px-4">
                                <div className="col-md-12">
                                    {renderFormControls(
                                        form,
                                        "committeeMember",
                                        {
                                            comment: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: TextAreaFormInput,
                                                inputDisabled: formDisabled,
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-sm-0",
                                                fieldClassName: "col-sm-12 generic-form-field",
                                                inputLabel: " ",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <Form
                model={reduxFormName}
                onSubmit={(val, _event) => handleOnSubmit(val)}
                onSubmitFailed={(val) => handleOnSubmitFailed(val)}
            >
                {renderFormControls(
                    form,
                    "committeeMember",
                    formComponents,
                    lookups,
                    validationFailures,
                    formProperties,
                    changeForm
                )}
                <div className="mt-4 mb-4 text-right">
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(`${reduxFormName}.saveAndReturn`, false);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit
                    </button>
                    <button
                        disabled={formDisabled}
                        onClick={() => {
                            changeForm(`${reduxFormName}.saveAndReturn`, true);
                        }}
                        type="submit"
                        className="btn btn-primary mr-2"
                    >
                        Submit And Return
                    </button>
                    <button
                        onClick={onCancel}
                        className="btn btn-primary mr-2"
                    >
                        Cancel
                    </button>
                </div>
            </Form>
        );
    }, [
        permissions,
        savingCommitteeMember,
        formState,
        form,
        personnelSearchLoader,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed
    ]);

    return (
        <RestrictedLayout
            loading={
                loadingCommitteeMember || 
                creatingCommitteeMember ||
                loadingCommittee
            }
            permission={Dtos.Permission.CommitteeMemberView}
        >
            { renderContent }
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<CommitteeMemberPageProps> => {
    let committeeMember: Dtos.CommitteeMember | undefined = !(state.committeeMember.formData instanceof Array)
        ? state.committeeMember.formData
        : undefined;
    let committee: Dtos.Committee | undefined = !(state.committees.formData instanceof Array)
        ? state.committees.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        committeeMember: committeeMember!,
        committee: committee!,

        loadingCommittee:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Pending,
        loadCommitteeSuccess:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Success,
        loadCommitteeFailure:
            state.committees.loadState &&
            state.committees.loadState.status == RequestState.Failure,
        loadCommitteeErrorCode: state.committees.loadState.errorCode,

        loadingCommitteeMember:
            state.committeeMember.loadState &&
            state.committeeMember.loadState.status == RequestState.Pending,
        loadCommitteeMemberSuccess:
            state.committeeMember.loadState &&
            state.committeeMember.loadState.status == RequestState.Success,
        loadCommitteeMemberFailure:
            state.committeeMember.loadState &&
            state.committeeMember.loadState.status == RequestState.Failure,
        loadCommitteeMemberErrorCode: state.committeeMember.loadState.errorCode,

        savingCommitteeMember:
            state.committeeMember.saveState &&
            state.committeeMember.saveState.status == RequestState.Pending,
        saveCommitteeMemberSuccess:
            state.committeeMember.saveState &&
            state.committeeMember.saveState.status == RequestState.Success,
        saveCommitteeMemberFailure:
            state.committeeMember.saveState &&
            state.committeeMember.saveState.status == RequestState.Failure,

        creatingCommitteeMember:
            state.committeeMember.createState &&
            state.committeeMember.createState.status == RequestState.Pending,
        createCommitteeMemberSuccess:
            state.committeeMember.createState &&
            state.committeeMember.createState.status == RequestState.Success,
        createCommitteeMemberFailure:
            state.committeeMember.createState &&
            state.committeeMember.createState.status == RequestState.Failure,

        validatingCommitteeMember:
            state.committeeMember.validateState &&
            state.committeeMember.validateState.status == RequestState.Pending,

        permissions: permissions,
        lookups: state.committeeMember.lookups,

        form: state.reduxForms.committeeMember,
        formState: state.committeeMember.formState,
        formProperties: state.committeeMember.formProperties,

        reduxFormState: state.reduxForms.formState.committeeMember,
        validationFailures: committeeMember
            ? state.committeeMember.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (dispatch): Partial<CommitteeMemberPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),

        loadCommitteeMember: bindActionCreators(CommitteeMemberActions.LoadCommitteeMemberById, dispatch),
        clearCommitteeMember: bindActionCreators(CommitteeMemberActions.Clear, dispatch),

        loadCommittee: bindActionCreators(CommitteeActions.LoadCommitteeById, dispatch),
        clearCommittee: bindActionCreators(CommitteeActions.Clear, dispatch),

        saveCommitteeMember: bindActionCreators(CommitteeMemberActions.SaveCommitteeMember, dispatch),
        createCommitteeMember: bindActionCreators(CommitteeMemberActions.CreateCommitteeMember, dispatch),
        validateCommitteeMember: bindActionCreators(CommitteeMemberActions.ValidateCommitteeMember, dispatch),

        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch)
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(CommitteeMemberPage);
